import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import useMeasure from 'react-use-measure';

import { ParameterNode } from '@data';
import { useTranslate } from '@i18n';
import { useLayoutCustomizations, useUiParameters } from '@queries';
import { layoutAtom, userSettingsAtom } from '@store';
import { colors, Spinner } from '@ui';

import { Parameter } from './Parameter';
import * as css from './Parameters.css';

type ParametersProps = {
    nodes: ParameterNode[] | undefined;
};

const GRID_GAP = 8;
const MIN_FLOOR_PARAMETER_WIDTH = 238;
const MIN_MODULE_WIDTH = MIN_FLOOR_PARAMETER_WIDTH + GRID_GAP;
const IDEAL_MODULE_WIDTH = 250;
const IDEAL_MODULE_HEIGHT = 175;
const CARD_HEIGHT_NO_CORRIDORS = 136;

export const Parameters = ({ nodes }: ParametersProps) => {
    const t = useTranslate();
    const layout = useAtomValue(layoutAtom);
    const userSettings = useAtomValue(userSettingsAtom);
    const derivedIdealModuleHeight =
        userSettings.showCorridors || userSettings.showGoal ? CARD_HEIGHT_NO_CORRIDORS : IDEAL_MODULE_HEIGHT;
    const { isLoading: isLoadingLayout } = useLayoutCustomizations(layout?.id || -1);
    const { isLoading: isLoadingParameters } = useUiParameters();
    const [ref, { height, width }] = useMeasure({ debounce: 100 });
    const dimensions = useMemo(() => {
        if (!width || !height) return { columns: 0, rows: 0, maxItems: 0 };

        let columns = Math.max(1, Math.floor(width / IDEAL_MODULE_WIDTH));
        let rows = Math.max(1, Math.floor(height / derivedIdealModuleHeight));

        if (columns === 1) {
            columns = Math.max(1, Math.floor(width / MIN_MODULE_WIDTH));
            rows = Math.max(1, Math.floor(height / derivedIdealModuleHeight));
        }

        return {
            columns,
            rows,
            maxItems: columns * rows,
        };
    }, [width, height, derivedIdealModuleHeight]);

    const style = useMemo(
        () => ({
            gridTemplateColumns: `repeat(${dimensions.columns}, 1fr)`,
            gridTemplateRows: `repeat(${dimensions.rows}, 1fr)`,
        }),
        [dimensions.columns, dimensions.rows],
    );

    if (dimensions.maxItems === 0 && (isLoadingLayout || isLoadingParameters)) {
        return (
            <div ref={ref} className={css.parametersLayout}>
                <Spinner size="l" color={colors.bluegray[600]} />
            </div>
        );
    }
    const visibleNodes = nodes?.slice(0, dimensions.maxItems) ?? [];

    return (
        <div ref={ref} className={css.parametersLayout} style={style}>
            {visibleNodes.map(({ id, i18n, parameter }) => (
                <Parameter key={id} name={t(i18n ?? '-')} parameter={parameter} />
            ))}
        </div>
    );
};
